import React, { useEffect, useState } from "react";
import {
  Grid,
  Pagination,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../components/Alert";
import ProcurementCard from "../../components/Biochar/ProcurementCard";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import { useTranslation } from "react-i18next";
import CustomAppBar from "../../components/AppBar";
import { getUserBasicDetailFromAuth } from "../../utils/common";
import { getProcurements } from "../../api/biomassProcurement";
import GenericButton from "../../components/Custom/GenericButton";

const ProcurementList = () => {
  const { role, user_code } = getUserBasicDetailFromAuth();

  const [procurements, setProcurements] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    status: "",
    name: "",
  });

  useEffect(() => {
    fetchProcurements(1);
  }, []);

  const fetchProcurements = async (page = 1) => {
    try {
      let farmer_code;
      if (role === "FARMER") {
        farmer_code = user_code;
      }

      let query = "";
      if (filters.name && role !== "FARMER") {
        query += `&farmer_name=${filters.name}`;
      }
      if (filters.status) {
        query += `&status=${filters.status}`;
      }

      const response = await getProcurements(farmer_code, page, query);
      const { results, total_pages, current_page_number } = response;

      if (isNotNullOrEmptyArray(results)) {
        setProcurements(results);
        setTotalPages(total_pages);
        setPage(current_page_number);
      } else {
        setProcurements([]);
        setTotalPages(1);
      }
    } catch (error) {
      showAlert("error", "Failed to fetch procurements");
      console.error("Error fetching procurements:", error);
    }
  };

  const handleChangePage = (event, value) => {
    setPage(value);
    fetchProcurements(value);
  };

  const handleAddNewProcurement = () => {
    navigate("/procurement-form");
  };

  const handleSearch = () => {
    fetchProcurements(1);
  };

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {role === "FARMER" && (
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddNewProcurement}
                fullWidth
              >
                {t("Add New Procurement")}
              </Button>
            </Grid>
          )}

          <Grid container item xs={12} spacing={2} alignItems="center">
            {(role === "VLE" || role === "AREA_MANAGER") && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label={t("Farmer Name")}
                  value={filters.name}
                  onChange={(e) =>
                    setFilters({ ...filters, name: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="status-label">{t("Status")}</InputLabel>
                <Select
                  labelId="status-label"
                  value={filters.status || ""}
                  label={t("Status")}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                >
                  <MenuItem value="">{t("All")}</MenuItem>
                  <MenuItem value="PLACED">{t("Placed")}</MenuItem>
                  <MenuItem value="INSPECTION_SCHEDULED">
                    {t("Inspection Scheduled")}
                  </MenuItem>
                  <MenuItem value="ACCEPTED">{t("Accepted")}</MenuItem>
                  <MenuItem value="REJECTED">{t("Rejected")}</MenuItem>
                  <MenuItem value="PICKUP_REQUESTED">
                    {t("Pickup Requested")}
                  </MenuItem>
                  <MenuItem value="PICKUP_SCHEDULED">
                    {t("Pickup Scheduled")}
                  </MenuItem>
                  <MenuItem value="PICKED">{t("Picked")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <GenericButton
                backgroundColor={"#4CAF50"}
                fontColor={"#fff"}
                fontSize={"14px"}
                padding={"15px 10px"}
                onClick={handleSearch}
              >
                {t("Search")}
              </GenericButton>
            </Grid>
          </Grid>

          {isNotNullOrEmptyArray(procurements) ? (
            procurements.map((procurement) => (
              <Grid item xs={12} sm={6} md={4} key={procurement.id}>
                <ProcurementCard
                  procurement={procurement}
                  navigate={navigate}
                />
              </Grid>
            ))
          ) : (
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <Typography variant="h6">{t("No procurements found")}</Typography>
            </Grid>
          )}

          {totalPages > 1 && (
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                color="primary"
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

export default ProcurementList;
